<template>
    <client-page>
        <page-section class="page-section--xs pt-0">
            <inventory-status details>
                <inventory-filter v-bind="{ loading }" />
            </inventory-status>
        </page-section>
        <page-section class="page-section--xs">
            <div class="pb-4 pb-md-5">
                <v-row justify="space-between" align="center">
                    <v-col cols="auto">
                        <h3 class="tit tit--xs">재고리스트</h3>
                    </v-col>
                    <v-col cols="auto">
                        <v-btn v-bind="{ loading }" color="grey lighten-3" outlined width="113" class="rounded-sm" @click="excel">
                            <span class="grey--text">엑셀 다운로드</span>
                        </v-btn>
                    </v-col>
                </v-row>
            </div>
            <v-simple-table :headers="headers" dense class="v-data-table--default v-data-table--no-hover inventory-table">
                <thead>
                    <tr>
                        <th v-for="header in headers" :key="header.value" :style="`width: calc(100% / ${headers.length})`">
                            {{ header.text }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in items" :key="item._id">
                        <td>
                            <span class="light-blue--text" v-if="item.type == PURCHASE_TYPES.STOCK.value">입고</span>
                            <span class="red--text" v-if="item.type == PURCHASE_TYPES.STOUT.value">출고</span>
                        </td>
                        <td>{{ item.fullName || "-" }}</td>
                        <td>{{ item.code || "-" }}</td>
                        <td>
                            <template v-if="item.type == PURCHASE_TYPES.STOCK.value">
                                {{ item.createdAt?.toDate?.() || "-" }}
                            </template>
                            <template v-else> - </template>
                        </td>
                        <td>
                            <template v-if="item.type == PURCHASE_TYPES.STOUT.value">
                                {{ item.createdAt?.toDate?.() || "-" }}
                            </template>
                            <template v-else> - </template>
                        </td>
                        <td>
                            <template v-if="item.type == PURCHASE_TYPES.STOCK.value">
                                {{ $dayjs?.(item.createdAt)?.add?.(90, "days")?.startOf?.("day")?.diff?.($dayjs?.()?.startOf?.("day"), "days") || "-" }}
                            </template>
                            <template v-else> - </template>
                        </td>
                        <td>
                            <template v-if="item.type == PURCHASE_TYPES.STOCK.value">
                                {{ ((item.amount || 1) * 1).format() }}
                            </template>

                            <template v-if="item.type == PURCHASE_TYPES.STOUT.value">
                                {{ ((item.amount || 1) * -1).format() }}
                            </template>
                        </td>
                        <td>
                            {{ item?.purchasePrice__unit?.format?.() }}
                        </td>
                        <td>
                            {{ item?.purchasePrice__total?.format?.() }}
                        </td>
                        <td>
                            <template v-if="item.type == PURCHASE_TYPES.STOCK.value">
                                {{ [item?.purchaseNo]?.filter((item) => !!item).join("\n") }}
                            </template>

                            <template v-if="item.type == PURCHASE_TYPES.STOUT.value">
                                {{ [item?.order?.receiver?.name, item?.purchaseNo]?.filter((item) => !!item).join("\n") }}
                            </template>
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>
            <div class="pagination-wrap pagination-wrap--sm">
                <v-pagination :value="page" :length="pageCount" :total-visible="11" color="primary" @input="(page) => $router.push({ query: { ...$route.query, page } })" />
            </div>
        </page-section>
        <page-section class="page-section--xs pb-0">
            <inventory-foot v-bind="{ summary }" />
        </page-section>
    </client-page>
</template>

<script>
import api from "@/api";
import dayjs from "@/plugins/vue-dayjs";
import { PURCHASE_CLAIM_STATUSES, PURCHASE_TYPES } from "@/assets/variables";

import ClientPage from "@/components/client/templates/client-page.vue";
import PageSection from "@/components/client/templates/page-section.vue";
import InventoryFoot from "@/components/client/mypage/inventory/inventory-foot.vue";
import InventoryFilter from "@/components/client/mypage/inventory/inventory-filter.vue";
import InventoryStatus from "@/components/client/mypage/inventory/inventory-status.vue";

const headers = [
    {
        value: "type",
        text: "입출고",
        formatter: (value) => PURCHASE_TYPES[value]?.text || "-",
    },
    {
        value: "fullName",
        text: "상품명",
    },
    {
        value: "code",
        text: "상품코드",
    },
    {
        value: "stockAt",
        text: "입고일",
        formatter: (value, item) => (item?.type == PURCHASE_TYPES.STOCK.value ? item?.createdAt?.toDateTime?.() || item?.createdAt || "-" : "-"),
    },
    {
        value: "stoutAt",
        text: "출고일",
        formatter: (value, item) => (item?.type == PURCHASE_TYPES.STOUT.value ? item?.createdAt?.toDateTime?.() || item?.createdAt || "-" : "-"),
    },
    {
        value: "createdAt",
        text: "잔여보관일",
        formatter: (value) => dayjs(value).add(90, "days").startOf("day").diff(dayjs().startOf("day"), "days"),
    },
    {
        value: "amount",
        text: "재고수량",
        formatter: (value = 0, item) => ({ [PURCHASE_TYPES.STOCK.value]: value, [PURCHASE_TYPES.STOUT.value]: value * -1 }[item?.type] || "-"),
    },
    {
        value: "purchasePrice__unit",
        text: "단가(원가)",
        formatter: (value) => value?.format?.() || value || "-",
    },
    {
        value: "purchasePrice__total",
        text: "총액(수량X단가)",
        formatter: (value) => value?.format?.() || value || "-",
    },
    {
        value: "purchaseNo",
        text: "비고",
        formatter: (value = 0, item) =>
            ({
                [PURCHASE_TYPES.STOCK.value]: [value]?.filter((item) => !!item).join("\n"),
                [PURCHASE_TYPES.STOUT.value]: [item?.order?.receiver?.name, value]?.filter((item) => !!item).join("\n"),
            }[item?.type] || "-"),
    },
];

export default {
    components: {
        ClientPage,
        PageSection,
        InventoryFoot,
        InventoryFilter,
        InventoryStatus,
    },
    setup: () => ({
        PURCHASE_TYPES,
        headers,
    }),
    data: () => ({
        purchases: [],

        limit: 10,
        summary: { totalCount: 0 },

        loading: false,
        showsSearch: true,
    }),
    computed: {
        items() {
            return [...this.purchases];
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        params() {
            let { ...query } = this.$route.query;

            query.type = JSON.stringify({
                $in: [PURCHASE_TYPES.STOCK.value, PURCHASE_TYPES.STOUT.value],
            });

            query.claimStatus = JSON.stringify({
                $nin: [PURCHASE_CLAIM_STATUSES.CANCEL_COMPLETED.value],
            });

            delete query.page;

            return { ...query };
        },
    },
    methods: {
        async init() {
            try {
                await this.search();
            } catch (error) {
                console.error(error);
            }
        },

        async search() {
            if (this.loading) return;
            else this.loading = true;

            try {
                let { skip, limit, params } = this;
                var { summary, purchases } = await api.v1.me.purchases.gets({
                    headers: { skip, limit, with_entire_amount: true, with_overall_price: true },
                    params,
                });

                this.summary = summary;
                this.purchases = purchases;
            } finally {
                this.loading = false;
            }
        },

        async excel() {
            if (this.loading) return;
            else this.loading = true;

            try {
                let { params } = this;
                let { purchases } = await api.v1.me.purchases.gets({ params });

                let fileName = `재고리스트`;
                this.$excel(purchases, this.headers, fileName);
            } finally {
                this.loading = false;
            }
        },
    },
    watch: {
        params() {
            this.search();
        },
        page() {
            this.search();
        },
    },
    mounted() {
        this.init();
    },
};
</script>

<style lang="scss" scoped>
.inventory-table {
    --border-color: var(--v-grey-lighten4);
    th,
    td {
        text-align: center !important;
        padding: 12px !important;
        &:not(:last-child) {
            border-right: 1px solid var(--border-color);
        }
    }
    th {
        white-space: nowrap;
    }
}
</style>
